// import node module libraries
import { Fragment } from "react";
import { Badge, Card, Col, Container, Form, ListGroup, Row } from "react-bootstrap";
import { Link } from "react-router-dom";

// import hooks
import useToggle from "hooks/useToggle";

// import custom components
import LogosTopHeading2 from "components/marketing/common/clientlogos/LogosTopHeading2";

// import sub components
import PricingCard from "./PricingCard";

// import data files
import LogoList1 from "data/marketing/clientlogos/LogoList1";
import FAQsData from "data/marketing/pricing/FAQsData";
import { individual, starter, team } from "data/marketing/pricing/PricingPlansData";

const Pricing = () => {
  const [Pricing, togglePricing] = useToggle(true);
  const stationPlans = [
    {
      id: "market-radar",
      title: "Market Radar",
      subtitle: "Monitoreo automático de precios de rivales",
      price: 299,
      unit: "usd / mes",
      accent: "primary",
      features: [
        "Historial de precios por rival",
        "Máximos / mínimos / promedios",
        "Alertas de cambios (email)",
        "Compara con tus competidores",
        "1 estación",
      ],
    },
    {
      id: "profit-optimizer",
      title: "Profit Optimizer",
      subtitle: "Maximiza utilidad con datos reales",
      price: 899,
      unit: "usd / mes",
      accent: "success",
      badge: "Recomendado",
      features: [
        "Market Radar",
        "+ Alertas por whatsapp",
        "Carga de ventas (control volumetrico)",
        "Cálculo de precio óptimo",
        "Vista calendario de resultados",
        "Dashboard financiero",
        "1 estación",
      ],
    },
    {
      id: "enterprise",
      title: "Enterprise",
      subtitle: "Custom pricing para volumen alto de estaciones",
      price: "Custom",
      unit: "Habla con ventas",
      accent: "dark",
      badge: "Alto volumen",
      features: [
        "Descuentos por volumen de estaciones",
        "Onboarding dedicado y soporte prioritario",
        "Capacitaciones para tu equipo",
        "Integraciones personalizadas (API)",
        "Reportes ejecutivos y SLAs",
        "Opciones de facturación a medida",
      ],
    },
  ];

  return (
    <Fragment>
      <section className="py-lg-13 py-8 bg-primary">
        <Container>
          {/* Page header */}
          <Row className="align-items-center">
            <Col xl={{ span: 8, offset: 2 }} lg={12} md={12} sm={12}>
              <div className="text-center mb-6 px-md-8">
                <h1 className="text-white display-3 fw-bold">
                  Simple pricing that scales with your business
                </h1>
                <p className="text-white lead mb-4">
                  Reference giving information on its origins, as well as a random Lipsum generator.
                </p>
                {/* Switch Toggle */}
                <div
                  id="pricing-switch"
                  className="d-flex justify-content-center align-items-center"
                >
                  <span className="text-white me-2">Monthly</span>
                  <Form>
                    <Form.Check
                      name="radios"
                      type="checkbox"
                      className="form-switch form-switch-price"
                      id="pricingSwitch"
                      checked={Pricing}
                      onChange={togglePricing}
                    />
                  </Form>
                  <span className="text-white ms-2">Yearly</span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Content */}
      <section className="mt-n8 pb-8">
        <Container>
          <Row>
            <Col lg={4} md={12} sm={12}>
              <PricingCard content={starter} pricingMode={Pricing} />
            </Col>
            <Col lg={4} md={12} sm={12}>
              <PricingCard content={individual} pricingMode={Pricing} />
            </Col>
            <Col lg={4} md={12} sm={12}>
              <PricingCard content={team} pricingMode={Pricing} />
            </Col>
          </Row>
        </Container>
      </section>

      {/* Station-focused pricing */}
      <section className="py-lg-12 py-8 bg-white">
        <Container>
          <Row className="mb-6">
            <Col lg={8} md={12} sm={12}>
              <span className="text-uppercase text-primary fw-bold small">
                Planes para estaciones
              </span>
              <h2 className="display-5 fw-bold mb-3">
                Pricing enfocado en monitorear y optimizar a tus rivales
              </h2>
              <p className="lead text-muted mb-0">
                Activa los planes que necesitas: monitorea precios con Market Radar o suma la
                inteligencia de Profit Optimizer para ajustar tus precios con datos reales.
              </p>
            </Col>
          </Row>
          <Row>
            {stationPlans.map((plan) => (
              <Col lg={6} md={12} sm={12} className="mb-4" key={plan.id}>
                <Card className="h-100 shadow-sm">
                  <Card.Body className="p-5 d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start mb-4">
                      <div>
                        <h3 className="mb-1">{plan.title}</h3>
                        <p className="text-muted mb-0">{plan.subtitle}</p>
                      </div>
                      {plan.badge && (
                        <Badge bg={plan.accent || "primary"} pill>
                          {plan.badge}
                        </Badge>
                      )}
                    </div>
                    <div className="d-flex align-items-baseline mb-4">
                      <span className="h1 fw-bold mb-0">${plan.price}</span>
                      <span className="text-muted ms-2">{plan.unit}</span>
                    </div>
                    <ListGroup bsPrefix="list-unstyled" className="mb-4">
                      {plan.features.map((feature, idx) => (
                        <ListGroup.Item key={idx} bsPrefix="list-item" className="mb-2">
                          <span className="text-success me-2">
                            <i className="far fa-check-circle"></i>
                          </span>
                          <span>{feature}</span>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                    <div className="mt-auto d-grid">
                      <Link to="#" className={`btn btn-${plan.accent || "primary"}`}>
                        Quiero este plan
                      </Link>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Client logo */}
      <LogosTopHeading2
        title="Loved by over 5 million users from companies like"
        logos={LogoList1}
      />

      {/* FAQ */}
      <section className="py-lg-10 py-5">
        <Container>
          <Row>
            {/* Row */}
            <Col md={12} sm={12}>
              <div className="mb-8 text-center">
                <h2 className="h1">Frequently Asked Questions</h2>
              </div>
            </Col>
          </Row>
          {/* Row */}
          <Row>
            {/* FAQs List */}
            {FAQsData.map((item, index) => {
              return (
                <Col lg={4} md={6} sm={12} className="mb-3" key={index}>
                  <h4>{item.question}</h4>
                  <p>{item.answer}</p>
                </Col>
              );
            })}
            {/* Col */}
            <Col md={12} sm={12} className="mt-lg-10 mt-4">
              {/* Card*/}
              <Card>
                {/* Card body */}
                <Card.Body>
                  <div className="d-lg-flex justify-content-between align-items-center">
                    <h4 className="mb-0">Have other questions?</h4>
                    <span>
                      Send us a mail via:{" "}
                      <Link to="https://codescandy.com/contact-us/" target="_blank">
                        support@example.com
                      </Link>
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </Fragment>
  );
};

export default Pricing;
